import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { StatCard, Card } from "@/admin/ui";
import { toast } from "sonner";
import {
  Users, UserPlus, CalendarDays, Megaphone, Tags, Car, AlertTriangle,
  ArrowRight, UserRoundPlus, Upload, ListPlus, CalendarPlus, ChevronRight, CheckCircle2,
} from "lucide-react";

export default function Overview() {
  const { eventId, event } = useAdminEvent();
  const [cc, setCc] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const load = React.useCallback(() => {
    if (!eventId) return;
    setCc(null); setErr(null);
    api.commandCenter(eventId)
      .then(setCc)
      .catch((e) => setErr(e?.response?.status === 403 ? "denied" : "error"));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <Card className="text-center py-10" data-testid="overview-error">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(38_70%_92%)] text-[hsl(38_55%_32%)] mb-3"><AlertTriangle className="h-6 w-6" /></div>
        <h3 className="font-serif text-xl text-[hsl(var(--primary))]">
          {err === "denied" ? "You don't have manager access to this event" : "Couldn't load the overview"}
        </h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-md mx-auto">
          {err === "denied"
            ? `Your account isn't a manager for ${event?.name || "this event"}. Choose an event you manage from the event selector.`
            : "Something went wrong loading this event's overview."}
        </p>
        {err !== "denied" && (
          <button onClick={load} data-testid="overview-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold">Retry</button>
        )}
      </Card>
    );
  }
  if (!cc) return <div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />;
  const s = cc.snapshot;
  const empty = s.attendees === 0 && s.sessions === 0 && s.groups === 0;

  return (
    <div>
      {/* Snapshot */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Attendees" value={s.attendees} icon={Users} />
        <StatCard label="Attending" value={s.rsvp_yes} icon={CheckCircle2} />
        <StatCard label="Groups" value={s.groups} icon={Tags} />
        <StatCard label="Sessions" value={s.sessions} icon={CalendarDays} accent="gold" />
        <StatCard label="Plus-ones" value={s.plus_ones} icon={UserPlus} />
        <StatCard label="Announcements" value={s.announcements} icon={Megaphone} />
        <StatCard label="Transfers" value={s.transfers_activated ? `${s.transfers_confirmed} / ${s.attendees}` : <span className="text-base font-sans text-[hsl(var(--muted-foreground))]" data-testid="transfers-not-activated">Not Activated</span>} icon={Car} />
      </div>

      {empty ? (
        <Card className="text-center py-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] mb-3"><ListPlus className="h-7 w-7" /></div>
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">Let's set up {event?.name}</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-md mx-auto">This is a fresh event workspace. Start by adding groups, importing attendees and building the schedule.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <QA label="Import Attendees" icon={Upload} onClick={() => navigate("/manager/data")} solid />
            <QA label="Create Group" icon={ListPlus} onClick={() => navigate("/manager/groups")} />
            <QA label="Add Session" icon={CalendarPlus} onClick={() => navigate("/manager/schedule")} />
          </div>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Readiness */}
          <Card>
            <h3 className="font-serif text-xl text-[hsl(var(--primary))] mb-3">Event Readiness</h3>
            <div className="space-y-3">
              {cc.readiness.map((r) => (
                <div key={r.label}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[hsl(var(--primary))] font-medium flex items-center gap-2">{r.label}</span>
                    {r.not_activated ? (
                      <span className="text-[10px] uppercase tracking-wide rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-2 py-0.5 font-semibold">Not Activated</span>
                    ) : (
                      <span className="text-[hsl(var(--muted-foreground))] font-semibold">{r.value}%</span>
                    )}
                  </div>
                  {r.not_activated ? (
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{r.note || "Awaiting final file"}</div>
                  ) : (
                    <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${r.value}%`, background: r.value >= 90 ? "hsl(var(--secondary))" : r.value >= 60 ? "hsl(var(--pc-gold))" : "hsl(var(--pc-gold-2))" }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Needs attention */}
          <Card>
            <h3 className="font-serif text-xl text-[hsl(var(--primary))] mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[hsl(var(--pc-gold))]" /> Needs Attention</h3>
            {cc.needs_attention.length === 0 ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">All clear — nothing needs attention.</div>
            ) : (
              <div className="space-y-2">
                {[...cc.needs_attention].sort((a, b) => (a.severity === "low" ? 1 : 0) - (b.severity === "low" ? 1 : 0)).map((n, i) => {
                  const low = n.severity === "low";
                  return (
                  <button key={i} onClick={() => navigate(`/manager/${n.to}`)} data-testid={`needs-item${low ? "-low" : ""}`} className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 text-left ${low ? "border-[hsl(var(--border)/0.6)] bg-[hsl(var(--muted)/0.3)] hover:bg-[hsl(var(--muted)/0.5)]" : "border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--muted)/0.5)]"}`}>
                    <span className={`text-sm ${low ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--primary))]"}`}>
                      <span className={`font-serif text-lg mr-1 ${low ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--pc-gold))]"}`}>{n.count}</span>{n.label}
                      {low && <span className="ml-2 align-middle text-[9px] uppercase tracking-wide rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-1.5 py-0.5 font-semibold">Low priority</span>}
                      {n.help && <span className="block text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{n.help}</span>}
                    </span>
                    <ChevronRight className={`h-4 w-4 shrink-0 ${low ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--secondary))]"}`} />
                  </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function QA({ label, icon: Icon, onClick, solid }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${solid ? "bg-[hsl(var(--primary))] text-white" : "border border-[hsl(var(--border))] bg-white text-[hsl(var(--primary))]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
